import React, { useState, useEffect } from 'react';
import './LoadingScreen.css';

interface VoltStatus {
  updateStatus: string | number | boolean;
  version: string;
}

interface LoadingScreenProps {
  onComplete?: () => void;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ onComplete }) => {
  const [status, setStatus] = useState<VoltStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [progress, setProgress] = useState(0);
  const [revealedText, setRevealedText] = useState('');
  const [scrambledPart, setScrambledPart] = useState('');

  const targetText = 'CONNECTED';
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

  // Calculate how many letters should be revealed based on progress
  const revealedCount = Math.floor((progress / 100) * targetText.length);
  
  // Calculate connection strength (0-5 bars)
  const connectionStrength = Math.min(5, Math.floor((progress / 100) * 5) + (progress > 0 ? 1 : 0));

  useEffect(() => {
    // Update revealed text based on progress
    setRevealedText(targetText.slice(0, revealedCount));
  }, [revealedCount]);

  useEffect(() => {
    // Scramble remaining unrevealed characters
    const scrambleInterval = setInterval(() => {
      const remaining = targetText.length - revealedCount;
      let scrambled = '';
      for (let i = 0; i < remaining; i++) {
        scrambled += chars[Math.floor(Math.random() * chars.length)];
      }
      setScrambledPart(scrambled);
    }, 40);

    return () => clearInterval(scrambleInterval);
  }, [revealedCount]);

  useEffect(() => {
    // Animate progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 85 && loading) return prev;
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + (loading ? 0.5 : 1.5);
      });
    }, 50);

    const fetchStatus = async () => {
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 3500));
      
      try {
        const [response] = await Promise.all([
          fetch('https://proxy-api-taupe.vercel.app/api/status/exploits/volt'),
          minLoadTime
        ]);
        const data = await response.json();
        setStatus({
          updateStatus: data.updateStatus,
          version: data.version
        });
      } catch (err) {
        await minLoadTime;
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();

    return () => clearInterval(progressInterval);
  }, [loading]);

  // Wait for progress to reach 100 before showing result
  useEffect(() => {
    if (!loading && progress >= 100 && !showResult) {
      setTimeout(() => {
        setShowResult(true);
        // After 3 seconds of showing result, call onComplete
        setTimeout(() => {
          if (onComplete) onComplete();
        }, 3000);
      }, 300);
    }
  }, [loading, progress, showResult, onComplete]);

  const getStatusColor = (updateStatus: string | number | boolean | undefined) => {
    // API returns boolean: true = updated, false = outdated
    if (updateStatus === true || updateStatus === 'true') return 'status-updated';
    if (updateStatus === false || updateStatus === 'false') return 'status-outdated';
    
    const statusStr = String(updateStatus || '').toLowerCase();
    switch (statusStr) {
      case 'updated':
      case '1':
        return 'status-updated';
      case 'updating':
      case '2':
        return 'status-updating';
      case 'outdated':
      case '0':
        return 'status-outdated';
      default:
        return 'status-unknown';
    }
  };

  const getStatusText = () => {
    if (error) return 'CONNECTION FAILED';
    const statusVal = status?.updateStatus;
    // API returns boolean: true = updated, false = outdated
    if (statusVal === true || statusVal === 'true') return 'UPDATED';
    if (statusVal === false || statusVal === 'false') return 'OUTDATED';
    if (statusVal === '1' || statusVal === 1 || String(statusVal).toLowerCase() === 'updated') return 'UPDATED';
    if (statusVal === '2' || statusVal === 2 || String(statusVal).toLowerCase() === 'updating') return 'UPDATING';
    if (statusVal === '0' || statusVal === 0 || String(statusVal).toLowerCase() === 'outdated') return 'OUTDATED';
    return 'UNKNOWN';
  };

  return (
    <div className="loading-screen">
      <div className="loading-content">
        <div className={`logo-container ${showResult ? 'logo-complete' : ''}`}>
          <img src="/bolt-full.svg" alt="Volt Logo" className="loading-logo" />
          <div className="logo-ring"></div>
        </div>
        
        {!showResult ? (
          <div className="loading-section">
            <div className="connection-display">
              <div className="scramble-text">
                <span className="revealed">{revealedText}</span>
                <span className="scrambled">{scrambledPart}</span>
              </div>
              <div className="connection-bars">
                {[0, 1, 2, 3, 4].map(i => (
                  <div 
                    key={i} 
                    className={`connection-bar ${connectionStrength > i ? 'active' : ''}`}
                  />
                ))}
              </div>
            </div>
            
            <div className="progress-section">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="result-section">
            <div className={`status-display ${!error && status ? getStatusColor(status.updateStatus) : 'status-unknown'}`}>
              <div className="status-label">STATUS</div>
              <div className="status-value">{getStatusText()}</div>
            </div>
            
            {!error && status?.version && (
              <div className="version-display">
                <span className="version-label">BUILD</span>
                <span className="version-value">{status.version}</span>
              </div>
            )}
            
            <div className="loading-next">
              <span className="next-dot"></span>
              <span>Entering dashboard...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingScreen;
